/**
 * Types for the settings of a Meteora DLMM pair.
 * Contract: PAIR_SETTINGS_CONTRACT_20261009.md, sections 1, 2 and 4.
 *
 * D1. A pair without a settings row has every engine off.
 * D2. A limit `null` means no limit. A limit `0` forbids the operation.
 * Every amount (lamports and base raw units) travels as a string of decimal digits.
 */

/** One switch for each engine of the pair. */
export const SOL_METEORA_ENGINE_FLAGS = [
  'swap_enabled',
  'batch_enabled',
  'buy_sell_enabled',
  'counter_trade_enabled',
  'balance_enabled',
  'transfer_enabled',
  'holders_enabled',
  'volume_regular_enabled',
  'volume_organic_enabled',
] as const;

/**
 * Engine switches that an older API does not return.
 * A missing switch of this list reads as `false`: the engine is off.
 */
export const SOL_METEORA_OPTIONAL_ENGINE_FLAGS: readonly SolMeteoraEngineFlag[] = [
  'volume_regular_enabled',
  'volume_organic_enabled',
];

/** Limits in lamports or in base raw units. `null` means no limit. */
export const SOL_METEORA_RAW_LIMITS = [
  'max_buy_lamports',
  'max_sell_base_raw',
  'ct_max_buy_lamports',
  'ct_max_sell_base_raw',
  'balance_max_refill_lamports',
  'balance_max_base_input_raw',
  'balance_max_threshold_margin_lamports',
  'transfer_max_total_lamports',
  'transfer_max_total_base_raw',
] as const;

/** Limits that are plain integers. `null` means no limit. */
export const SOL_METEORA_INT_LIMITS = [
  'max_slippage_bps',
  'max_wallets',
  'transfer_max_destinations',
] as const;

/** Technical fields (D3). The database column gives the default value. They are never `null`. */
export const SOL_METEORA_TECHNICAL_FIELDS = [
  'max_fee_lamports',
  'max_rent_lamports',
  'min_sol_reserve_lamports',
  'balance_max_network_fee_lamports',
] as const;

export type SolMeteoraEngineFlag = (typeof SOL_METEORA_ENGINE_FLAGS)[number];
export type SolMeteoraRawLimit = (typeof SOL_METEORA_RAW_LIMITS)[number];
export type SolMeteoraIntLimit = (typeof SOL_METEORA_INT_LIMITS)[number];
export type SolMeteoraTechnicalField = (typeof SOL_METEORA_TECHNICAL_FIELDS)[number];

/** The fields that a user can change. `GET` returns them and `PUT` sends them. */
export type SolMeteoraPairSettingsValues = { [K in SolMeteoraEngineFlag]: boolean } & {
  [K in SolMeteoraRawLimit]: string | null;
} & { [K in SolMeteoraIntLimit]: number | null } & { [K in SolMeteoraTechnicalField]: string };

/** Parsed body of `GET /api/v1/sol-bot/pair/:pair_id/meteora-settings`. */
export type SolMeteoraPairSettings = SolMeteoraPairSettingsValues & {
  pair_id: number;
  /** `false` when the pair has no settings row. Then `version` is 0 and every engine is off. */
  exists: boolean;
  version: number;
  /** The API can omit the time and the author of the last change. */
  updated_at: string | null;
  updated_by: number | null;
};

/**
 * Body of `PUT /api/v1/sol-bot/pair/:pair_id/meteora-settings`.
 * The pair id travels in the path. `expected_version` is 0 for a new row.
 */
export type SolMeteoraPairSettingsPutRequest = SolMeteoraPairSettingsValues & {
  expected_version: number;
};

export type SolMeteoraPairSettingsApiError = {
  /** HTTP status. `null` when the request had no response. */
  status: number | null;
  /** String code of the server, for example `SETTINGS_VERSION_CONFLICT`. */
  code: string | null;
  message: string;
};

export type SolMeteoraPairSettingsApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: SolMeteoraPairSettingsApiError };
